import { UserRole } from "@/types/enums";

import { User } from "@/types";
import { ceilRupee } from "@/lib/utils";

export {
  ceilRupee,
  getPayableTotal,
  formatCurrency,
  formatRupeeExact,
  formatWalletRupee,
  getAvailableCredit,
} from "@/lib/utils";

/** Payable / headline totals: ceil to whole rupee. */
export function formatToRupee(amount: number) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(ceilRupee(amount));
}

/** True when checkout has a real booking-for user object (not null / []). */
export function isSelectedHotelUser(selectedUser: unknown): boolean {
  if (selectedUser == null || typeof selectedUser !== "object" || Array.isArray(selectedUser)) {
    return false;
  }
  return Boolean((selectedUser as { id?: unknown }).id);
}

/**
 * A hotel counts as domestic when its address resolves to India. The country code
 * can be missing when provider static content is unavailable, so fall back to name.
 */
export function isDomesticHotel(hInfo: any): boolean {
  const country = hInfo?.ad?.country;
  const code = String(country?.code || "").trim().toUpperCase();
  if (code) {
    return code === "IN";
  }
  const name = String(country?.name || hInfo?.ad?.cn || "").trim().toLowerCase();
  return name === "india";
}

/**
 * Stay total for markup. v3 review stamps option.tp onto every room.tfcs.TF;
 * summing those rooms would multiply the stay price.
 */
export function getHotelStayBaseFare(
  option?: { tp?: number; ris?: Array<{ tfcs?: { TF?: number } }> } | null,
  rooms?: Array<{ tfcs?: { TF?: number } }> | null
): number {
  const roomsInfo = rooms || option?.ris || [];
  const optionTotal = Number(option?.tp) || 0;
  const summed = roomsInfo.reduce(
    (acc, room) => acc + (Number(room?.tfcs?.TF) || 0),
    0
  );

  if (optionTotal > 0 && summed > optionTotal) {
    return optionTotal;
  }
  return summed || optionTotal;
}

/**
 * Markup in rupees. Backend stores ops[0].markup as an amount.
 * Selected-user hotelMarkup.domestic/international is a percent. Never mix them.
 */
export function getHotelMarkupAmount({
  selectedUser,
  optionMarkup,
  baseFare,
  isDomestic,
}: {
  selectedUser: any;
  optionMarkup?: number;
  baseFare: number;
  isDomestic: boolean;
}): number {
  const backendMarkup = ceilRupee(Number(optionMarkup) || 0);
  if (!isSelectedHotelUser(selectedUser)) {
    return backendMarkup;
  }

  const rawPercent =
    selectedUser?.hotelMarkup?.[isDomestic ? "domestic" : "international"];
  if (rawPercent == null || rawPercent === "") {
    return backendMarkup;
  }

  const percent = Number(rawPercent);
  if (!Number.isFinite(percent)) {
    return backendMarkup;
  }

  return ceilRupee((baseFare * percent) / 100);
}

/** Nights from pis (legacy), else check-in/out on room or searchQuery. */
export function getStayNights(
  room?: {
    pis?: unknown[];
    checkInDate?: string;
    checkOutDate?: string;
  } | null,
  searchQuery?: {
    checkinDate?: string;
    checkoutDate?: string;
  } | null
): number {
  if (Array.isArray(room?.pis) && room.pis.length > 0) {
    return room.pis.length;
  }

  const checkIn = room?.checkInDate || searchQuery?.checkinDate;
  const checkOut = room?.checkOutDate || searchQuery?.checkoutDate;
  if (!checkIn || !checkOut) return 0;

  const start = new Date(checkIn);
  const end = new Date(checkOut);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0;

  const nights = Math.round(
    (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)
  );
  return nights > 0 ? nights : 0;
}

/**
 * Parse provider fields that may be JSON objects OR plain strings.
 * Returns parsed value on success; otherwise returns the original string / fallback.
 */
export function parseMaybeJson<T = unknown>(
  value: unknown,
  fallback: T | null = null
): T | unknown {
  if (value == null || value === "") return fallback;
  if (typeof value !== "string") return value;

  const trimmed = value.trim();
  if (!trimmed) return fallback;

  // Skip parse for clearly non-JSON prose (avoids throw on "Free Wireless...")
  const looksLikeJson =
    trimmed.startsWith("{") ||
    trimmed.startsWith("[") ||
    (trimmed.startsWith('"') && trimmed.endsWith('"'));
  if (!looksLikeJson) return value;

  try {
    return JSON.parse(trimmed) as T;
  } catch {
    return fallback !== null ? fallback : value;
  }
}

/** Room amenity list: legacy fcs, else rexb BENEFIT values. */
export function getRoomFacilities(roomInfo?: {
  fcs?: string[];
  rexb?: Record<string, { values?: string[] }[]>;
} | null): string[] {
  if (Array.isArray(roomInfo?.fcs) && roomInfo.fcs.length > 0) {
    return roomInfo.fcs;
  }

  const benefits = roomInfo?.rexb?.BENEFIT;
  if (!Array.isArray(benefits)) return [];

  return benefits.flatMap((item) =>
    Array.isArray(item?.values) ? item.values : []
  );
}

export function formatDuration(minutes: number) {
    const hours = Math.floor(minutes / 60); // Get the number of hours
    const remainingMinutes = minutes % 60; // Get the remaining minutes

    return `${hours}h ${remainingMinutes}m`;
  }

  export const handleScrollUp = (scrollPosition: number) => {
    if (window.scrollY < scrollPosition) {
      window.scrollTo({
        top: scrollPosition,
        behavior: "smooth",
      });
    }
  };

export const updateDateLocaleString = (data: string,count:number ,type:"add" | "delete") => {
    const newDate = new Date(data);
    if(type === "add"){
        newDate.setDate(newDate.getDate() + count);
    }else{
        newDate.setDate(newDate.getDate() - count);
    }
    return newDate.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }

  export const toCapitalize = (str: string) => {
    str = str.toLowerCase();
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  export const toCapitalizeString = (str: string) => {
    return str.split(' ').map(word => toCapitalize(word)).join(' ');
  }

  export const subStringWithEllipsis = (str: string, maxLength: number) => {
    return str.length > maxLength ? str.substring(0, maxLength) + '...' : str;
  }
 
  export const convertDateToFormat = (date: string) => {
    const newDate = new Date(date);
    return newDate.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }

  export const formatDateAndTime = (dateTimeString: string) => {
    const dateObj = new Date(dateTimeString);
    const day = dateObj.getDate();
    const month = dateObj.toLocaleString("default", { month: "short" });
    const formattedDate = `${day} ${month}`;
    const hours = dateObj.getHours().toString().padStart(2, "0");
    const minutes = dateObj.getMinutes().toString().padStart(2, "0");
    const formattedTime = `${hours}:${minutes}`;
    return { formattedDate, formattedTime };
  };

  export const isSuperAdmin = (user: User) => {
    return ['SUPER_ADMIN'].includes(user?.role_type as UserRole);
  }

  export const convertToTFBookingId = (bookingId: string) => {
    return bookingId.replace("TJ", "TF");
  }

  export const convertToTJBookingId = (bookingId: string) => {
    return bookingId.replace("TF", "TJ");
  }
